// Mikla agent avatar — a CSS-drawn pink "m." mark. Per Direction B handoff:
// no image asset; pink (`#ff0041`) circle with the white "m" letterform from
// Geist Sans + a tiny period dot. The wordmark glyph SVG (MIKLA_ICON_RED_SVG)
// is reserved for the post-launch `m.` brand mark in marketing surfaces;
// the chatbot uses this lighter CSS treatment because it appears repeatedly
// (header avatar + every assistant message group avatar slot) and the
// SVG'd wordmark is heavier visual weight than the bubble line work warrants.
//
// Two sizes ship: 'md' = 36×36 (header) and 'sm' = 28×28 (message-group avatar).

import { useState } from 'react'

export interface MiklaAvatarProps {
  size?: 'sm' | 'md'
  // Vendor logo URL (chat_config branding). Falls back to the m. mark when
  // absent or when the image fails to load.
  logoUrl?: string | null
}

export function MiklaAvatar({ size = 'md', logoUrl }: MiklaAvatarProps) {
  // Track WHICH url failed (not a boolean): ChatPanel persists across the
  // form->chat phase transition, so a transient failure on the probe-phase
  // logo must not permanently pin the avatar to the m. mark — a changed
  // logo_url gets a fresh <img> attempt.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const imgFailed = logoUrl != null && logoUrl === failedUrl
  const dimensions = size === 'sm' ? 'w-7 h-7' : 'w-9 h-9'
  const fontSize = size === 'sm' ? 'text-xs' : 'text-[15px]'
  // The white period dot — sized to read at both 28 and 36, anchored to the
  // baseline of the "m" so the optical spacing matches the wordmark.
  const dotSize = size === 'sm' ? 'w-[2.5px] h-[2.5px]' : 'w-[3px] h-[3px]'

  if (logoUrl && !imgFailed) {
    return (
      <img
        data-testid="mikla-avatar"
        src={logoUrl}
        alt=""
        aria-hidden="true"
        onError={() => setFailedUrl(logoUrl)}
        className={`${dimensions} rounded-full object-cover flex-shrink-0 bg-white`}
      />
    )
  }

  return (
    <div
      data-testid="mikla-avatar"
      className={`${dimensions} rounded-full bg-accent text-white flex items-center justify-center font-semibold ${fontSize} flex-shrink-0`}
      style={{ letterSpacing: '-0.04em' }}
      aria-hidden="true"
    >
      <span className="inline-flex items-baseline gap-0 leading-none">
        m
        <span
          className={`${dotSize} rounded-full bg-white self-end ml-[1px] mb-[2px]`}
        />
      </span>
    </div>
  )
}
